import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import SendIcon from '@mui/icons-material/Send';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  sendWhatsAppTemplate,
  SendWhatsAppTemplateResponse,
  WhatsAppTemplate,
} from '../services/api';
import { CsvRecipient, parseCsvRecipients } from '../utils/parseCsvRecipients';
import { statusChipColor } from './statusChipColor';
import WhatsAppPreview from './WhatsAppPreview';

interface SendTemplateDialogProps {
  open: boolean;
  template: WhatsAppTemplate | null;
  onClose: () => void;
  onSent?: (response: SendWhatsAppTemplateResponse) => void;
}

const DEFAULT_COUNTRY_CODE = '65';

/**
 * Strip non-digits and prepend the default country code if it is missing.
 * Mirrors the backend `normalizeMobile` so the preview matches what gets sent.
 */
function normalizeMobile(mobile: string): string {
  const digits = (mobile || '').trim().replace(/^\+/, '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.startsWith(DEFAULT_COUNTRY_CODE)) return digits;
  return `${DEFAULT_COUNTRY_CODE}${digits}`;
}

export default function SendTemplateDialog({
  open,
  template,
  onClose,
  onSent,
}: SendTemplateDialogProps) {
  const [purpose, setPurpose] = useState('');
  const [csvFileName, setCsvFileName] = useState<string | null>(null);
  const [recipients, setRecipients] = useState<CsvRecipient[]>([]);
  const [csvError, setCsvError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SendWhatsAppTemplateResponse | null>(null);

  useEffect(() => {
    if (!open) {
      // Reset state when dialog closes.
      setPurpose('');
      setCsvFileName(null);
      setRecipients([]);
      setCsvError(null);
      setError(null);
      setSuccess(null);
      setSubmitting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [open]);

  /**
   * Highest {{N}} placeholder index found in the template body. Admins fill a
   * single `var1..varN` column set in the CSV; headers are image-only and so
   * do not contribute to the variable count.
   */
  const requiredVarCount = useMemo(() => {
    const matches: string[] = (template?.message || '').match(/\{\{(\d+)\}\}/g) || [];
    let max = 0;
    for (const token of matches) {
      const n = Number(token.replace(/[^\d]/g, ''));
      if (Number.isFinite(n) && n > max) max = n;
    }
    return max;
  }, [template?.message]);

  const validRecipients = useMemo(
    () => recipients.filter((r) => (r.ceaNumber || '').trim().length > 0 && r.mobile.trim().length > 0),
    [recipients],
  );
  const invalidRecipientsCount = recipients.length - validRecipients.length;

  /** Row numbers (1-indexed) that are missing one or more required variable values. */
  const missingVarRows = useMemo(() => {
    if (requiredVarCount === 0) return [] as number[];
    const missing: number[] = [];
    validRecipients.forEach((r, idx) => {
      const vars = r.variables ?? [];
      const hasAll =
        vars.length >= requiredVarCount &&
        vars
          .slice(0, requiredVarCount)
          .every((v) => typeof v === 'string' && v.trim().length > 0);
      if (!hasAll) missing.push(idx + 1);
    });
    return missing;
  }, [validRecipients, requiredVarCount]);

  const canSubmit = useMemo(
    () =>
      purpose.trim().length > 0 &&
      validRecipients.length > 0 &&
      missingVarRows.length === 0 &&
      !submitting &&
      template !== null,
    [purpose, validRecipients, missingVarRows, submitting, template],
  );

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    setCsvError(null);
    setError(null);
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = parseCsvRecipients(text);
      setCsvFileName(file.name);
      setRecipients(parsed);
      if (parsed.length === 0) {
        setCsvError('No recipients found in the CSV file.');
      }
    } catch (e) {
      setCsvError(e instanceof Error ? e.message : 'Failed to read CSV file.');
      setCsvFileName(null);
      setRecipients([]);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /**
   * Build and download a sample CSV with just the required headers
   * (`mobile`, `CEANumber`, and `var1`..`varN` when the template has
   * placeholders). No data rows are included.
   */
  const handleDownloadSampleCsv = () => {
    const headers = ['CEANumber', 'mobile'];
    for (let i = 1; i <= requiredVarCount; i += 1) headers.push(`var${i}`);
    const csv = `${headers.join(',')}\n`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const safeName = (template?.name || 'template').replace(/[^a-zA-Z0-9_-]/g, '_');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${safeName}_recipients_sample.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleClearCsv = () => {
    setCsvFileName(null);
    setRecipients([]);
    setCsvError(null);
  };

  const handleSubmit = async () => {
    if (!template || !canSubmit) return;
    setError(null);
    setSuccess(null);
    setSubmitting(true);
    try {
      const response = await sendWhatsAppTemplate(template.id, {
        purpose: purpose.trim(),
        recipients: validRecipients.map((r) => ({
          mobile: normalizeMobile(r.mobile),
          ceaNumber: (r.ceaNumber || '').trim(),
          ...(requiredVarCount > 0 && r.variables
            ? { variables: r.variables.slice(0, requiredVarCount) }
            : {}),
        })),
      });
      setSuccess(response);
      onSent?.(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send template.');
    } finally {
      setSubmitting(false);
    }
  };

  const previewRecipients = recipients.slice(0, 5);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Send template
          </Typography>
          {template && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
                {template.name}
              </Typography>
              <Chip
                size="small"
                label={template.status}
                color={statusChipColor(template.status)}
                variant="outlined"
              />
            </Stack>
          )}
        </Box>
        <IconButton onClick={onClose} disabled={submitting} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {template && (
          <Box sx={{ mb: 2.5 }}>
            <Typography
              variant="overline"
              sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
            >
              Message preview
            </Typography>
            <WhatsAppPreview
              body={template.message}
              imageUrl={template.imageUrl ?? undefined}
            />
          </Box>
        )}

        <TextField
          label="Purpose"
          required
          fullWidth
          value={purpose}
          onChange={(e) => setPurpose(e.target.value)}
          placeholder="e.g. Aug new launch broadcast"
          disabled={submitting}
          inputProps={{ maxLength: 100 }}
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Upload a CSV with a <strong>mobile</strong> column and a <strong>CEANumber</strong>{' '}
          column (both required). Country code <code>{DEFAULT_COUNTRY_CODE}</code> is
          auto-prepended to mobile numbers when missing.
          {requiredVarCount > 0 && (
            <>
              <br />
              This template has <strong>{requiredVarCount}</strong> variable
              {requiredVarCount === 1 ? '' : 's'}. Include column
              {requiredVarCount === 1 ? '' : 's'}{' '}
              <strong>
                {requiredVarCount === 1 ? 'var1' : `var1 - var${requiredVarCount}`}
              </strong>
              .
            </>
          )}
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            sx={{ textTransform: 'none' }}
            disabled={submitting}
          >
            Upload CSV
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={handleFileChange}
            />
          </Button>
          <Button
            variant="text"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadSampleCsv}
            disabled={submitting || !template}
            sx={{ textTransform: 'none' }}
          >
            Download sample CSV
          </Button>
          {csvFileName && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">
                <strong>{csvFileName}</strong> &mdash; {recipients.length} recipient
                {recipients.length === 1 ? '' : 's'}
              </Typography>
              <Button
                size="small"
                color="inherit"
                onClick={handleClearCsv}
                sx={{ textTransform: 'none' }}
                disabled={submitting}
              >
                Remove
              </Button>
            </Stack>
          )}
        </Stack>

        {csvError && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {csvError}
          </Alert>
        )}

        {invalidRecipientsCount > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {invalidRecipientsCount} row{invalidRecipientsCount === 1 ? '' : 's'} will be
            skipped (missing mobile and/or CEANumber).
          </Alert>
        )}

        {requiredVarCount > 0 && missingVarRows.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Template requires {requiredVarCount} variable
            {requiredVarCount === 1 ? '' : 's'} (
            <strong>
              {Array.from({ length: requiredVarCount }, (_, i) => `var${i + 1}`).join(', ')}
            </strong>
            ). {missingVarRows.length} row{missingVarRows.length === 1 ? '' : 's'} are
            missing values: row{missingVarRows.length === 1 ? '' : 's'}{' '}
            {missingVarRows.slice(0, 5).join(', ')}
            {missingVarRows.length > 5 ? `, … (+${missingVarRows.length - 5} more)` : ''}.
            Fix the CSV and re-upload before sending.
          </Alert>
        )}

        {previewRecipients.length > 0 && (
          <TableContainer
            sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
          >
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Mobile (sent)</TableCell>
                  <TableCell>CEANumber</TableCell>
                  {Array.from({ length: requiredVarCount }, (_, i) => (
                    <TableCell key={`var-h-${i}`}>{`var${i + 1}`}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {previewRecipients.map((r, idx) => (
                  <TableRow key={`${r.mobile}-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {normalizeMobile(r.mobile) || r.mobile}
                    </TableCell>
                    <TableCell
                      sx={{
                        color: (r.ceaNumber || '').trim() ? 'inherit' : 'error.main',
                        fontWeight: (r.ceaNumber || '').trim() ? 400 : 600,
                      }}
                    >
                      {r.ceaNumber || 'missing'}
                    </TableCell>
                    {Array.from({ length: requiredVarCount }, (_, i) => {
                      const value = r.variables?.[i] ?? '';
                      const hasValue = value.trim().length > 0;
                      return (
                        <TableCell
                          key={`var-${idx}-${i}`}
                          sx={{
                            color: hasValue ? 'inherit' : 'error.main',
                            fontWeight: hasValue ? 400 : 600,
                          }}
                        >
                          {hasValue ? value : 'missing'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {recipients.length > previewRecipients.length && (
              <Box sx={{ px: 2, py: 1, color: 'text.secondary' }}>
                <Typography variant="caption">
                  Showing first {previewRecipients.length} of {recipients.length} rows ({validRecipients.length} valid).
                </Typography>
              </Box>
            )}
          </TableContainer>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Broadcast scheduled for <strong>{success.recipientCount}</strong> recipient
            {success.recipientCount === 1 ? '' : 's'}.
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!canSubmit}
            startIcon={
              submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <SendIcon />
            }
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#1DA851' } }}
          >
            {submitting ? 'Sending…' : 'Send'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
